import React, { useEffect, useState, useCallback } from 'react';
import {
  Shield, AlertTriangle, Monitor, Smartphone, Tablet,
  Globe, Clock, Search, RefreshCw, ChevronDown, ChevronUp,
  Activity, TrendingUp, Users, MapPin, Chrome, Wifi,
  CheckCircle, XCircle, Building2, Filter
} from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';

interface LoginRecord {
  id: string;
  user_id: string | null;
  user_type: string;
  email: string;
  ip_address: string;
  user_agent: string;
  device_type: string;
  browser: string;
  os: string;
  success: boolean;
  failure_reason: string;
  session_id: string;
  company_id: string | null;
  company_name: string;
  created_at: string;
}

interface GroupedUser {
  email: string;
  user_id: string | null;
  display_name: string;
  company_name: string;
  company_id: string | null;
  records: LoginRecord[];
  last_login: string;
  unique_ips: string[];
  failed_count: number;
  success_count: number;
  alert_level: 'none' | 'warning' | 'danger';
}

interface CompanyGroup {
  company_name: string;
  company_id: string | null;
  users: GroupedUser[];
  total_logins: number;
  unique_ips: string[];
  failed_count: number;
}

interface Props {
  userType: 'consultant' | 'workday_client';
}

const ALERT_THRESHOLDS = {
  multipleIps: 5,
  failedAttempts: 3,
  failedWindow: 24, // hours
};

function formatRelativeTime(dateStr: string): string {
  const now = new Date();
  const date = new Date(dateStr);
  const diff = Math.floor((now.getTime() - date.getTime()) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function DeviceIcon({ type }: { type: string }) {
  if (type === 'mobile') return <Smartphone className="w-3.5 h-3.5" />;
  if (type === 'tablet') return <Tablet className="w-3.5 h-3.5" />;
  return <Monitor className="w-3.5 h-3.5" />;
}

function computeAlertLevel(user: Omit<GroupedUser, 'alert_level'>): 'none' | 'warning' | 'danger' {
  const recentWindow = Date.now() - ALERT_THRESHOLDS.failedWindow * 3600 * 1000;
  const recentFailed = user.records.filter(
    r => !r.success && new Date(r.created_at).getTime() > recentWindow
  ).length;

  if (recentFailed >= ALERT_THRESHOLDS.failedAttempts * 2) return 'danger';
  if (recentFailed >= ALERT_THRESHOLDS.failedAttempts) return 'warning';
  if (user.unique_ips.length >= ALERT_THRESHOLDS.multipleIps) return 'warning';
  return 'none';
}

function groupRecords(records: LoginRecord[]): CompanyGroup[] {
  const userMap = new Map<string, Omit<GroupedUser, 'alert_level'>>();

  for (const r of records) {
    const key = r.email || r.user_id || 'unknown';
    if (!userMap.has(key)) {
      userMap.set(key, {
        email: r.email,
        user_id: r.user_id,
        display_name: r.email,
        company_name: r.company_name || 'No Company',
        company_id: r.company_id,
        records: [],
        last_login: r.created_at,
        unique_ips: [],
        failed_count: 0,
        success_count: 0,
      });
    }
    const u = userMap.get(key)!;
    u.records.push(r);
    if (new Date(r.created_at) > new Date(u.last_login)) u.last_login = r.created_at;
    if (!u.unique_ips.includes(r.ip_address) && r.ip_address && r.ip_address !== 'unknown') {
      u.unique_ips.push(r.ip_address);
    }
    if (r.success) u.success_count++;
    else u.failed_count++;
  }

  const users: GroupedUser[] = Array.from(userMap.values()).map(u => ({
    ...u,
    alert_level: computeAlertLevel(u),
  }));

  const companyMap = new Map<string, CompanyGroup>();
  for (const u of users) {
    const cKey = u.company_id || u.company_name || 'No Company';
    if (!companyMap.has(cKey)) {
      companyMap.set(cKey, {
        company_name: u.company_name,
        company_id: u.company_id,
        users: [],
        total_logins: 0,
        unique_ips: [],
        failed_count: 0,
      });
    }
    const c = companyMap.get(cKey)!;
    c.users.push(u);
    c.total_logins += u.records.length;
    c.failed_count += u.failed_count;
    for (const ip of u.unique_ips) {
      if (!c.unique_ips.includes(ip)) c.unique_ips.push(ip);
    }
  }

  return Array.from(companyMap.values()).sort((a, b) =>
    a.company_name.localeCompare(b.company_name)
  );
}

export default function LoginSecurityTab({ userType }: Props) {
  const [records, setRecords] = useState<LoginRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterAlert, setFilterAlert] = useState<'all' | 'warning' | 'danger'>('all');
  const [expandedCompany, setExpandedCompany] = useState<string | null>(null);
  const [expandedUser, setExpandedUser] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<'24h' | '7d' | '30d' | 'all'>('7d');
  const [refreshing, setRefreshing] = useState(false);

  const fetchRecords = useCallback(async () => {
    setRefreshing(true);
    try {
      let query = supabase
        .from('login_records')
        .select('*')
        .eq('user_type', userType)
        .order('created_at', { ascending: false })
        .limit(2000);

      if (dateRange !== 'all') {
        const hours = dateRange === '24h' ? 24 : dateRange === '7d' ? 168 : 720;
        const cutoff = new Date(Date.now() - hours * 3600 * 1000).toISOString();
        query = query.gte('created_at', cutoff);
      }

      const { data, error } = await query;
      if (!error && data) setRecords(data);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [userType, dateRange]);

  useEffect(() => { fetchRecords(); }, [fetchRecords]);

  const companyGroups = groupRecords(records);

  const filteredGroups = companyGroups
    .map(g => ({
      ...g,
      users: g.users.filter(u => {
        const matchSearch = !search ||
          u.email.toLowerCase().includes(search.toLowerCase()) ||
          u.company_name.toLowerCase().includes(search.toLowerCase()) ||
          u.unique_ips.some(ip => ip.includes(search));
        const matchAlert = filterAlert === 'all' || u.alert_level === filterAlert;
        return matchSearch && matchAlert;
      }),
    }))
    .filter(g => g.users.length > 0);

  const totalLogins = records.length;
  const totalFailed = records.filter(r => !r.success).length;
  const totalAlerts = companyGroups.flatMap(g => g.users).filter(u => u.alert_level !== 'none').length;
  const uniqueIps = [...new Set(records.map(r => r.ip_address).filter(Boolean))].length;

  const browserStats = records.reduce<Record<string, number>>((acc, r) => {
    if (r.browser) acc[r.browser] = (acc[r.browser] || 0) + 1;
    return acc;
  }, {});
  const topBrowser = Object.entries(browserStats).sort((a, b) => b[1] - a[1])[0]?.[0] || '—';

  return (
    <div className="space-y-6">
      {/* Analytics Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-gray-100 p-4 flex items-start gap-3">
          <div className="p-2 rounded-lg bg-teal-50">
            <Activity className="w-5 h-5 text-[#40E0D0]" />
          </div>
          <div>
            <p className="text-2xl font-bold text-gray-900">{totalLogins.toLocaleString()}</p>
            <p className="text-xs text-gray-500 mt-0.5">Total Logins</p>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-4 flex items-start gap-3">
          <div className="p-2 rounded-lg bg-red-50">
            <XCircle className="w-5 h-5 text-red-500" />
          </div>
          <div>
            <p className="text-2xl font-bold text-gray-900">{totalFailed}</p>
            <p className="text-xs text-gray-500 mt-0.5">Failed Attempts</p>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-4 flex items-start gap-3">
          <div className="p-2 rounded-lg bg-amber-50">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
          </div>
          <div>
            <p className="text-2xl font-bold text-gray-900">{totalAlerts}</p>
            <p className="text-xs text-gray-500 mt-0.5">Security Alerts</p>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-4 flex items-start gap-3">
          <div className="p-2 rounded-lg bg-blue-50">
            <Globe className="w-5 h-5 text-blue-500" />
          </div>
          <div>
            <p className="text-2xl font-bold text-gray-900">{uniqueIps}</p>
            <p className="text-xs text-gray-500 mt-0.5">Unique IPs</p>
          </div>
        </div>
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Login Success Rate</p>
          <div className="flex items-end gap-2">
            <span className="text-2xl font-bold text-gray-900">
              {totalLogins > 0 ? Math.round(((totalLogins - totalFailed) / totalLogins) * 100) : 0}%
            </span>
            <span className="text-sm text-gray-500 mb-0.5">of attempts</span>
          </div>
          <div className="mt-2 h-2 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-[#40E0D0] rounded-full transition-all"
              style={{ width: `${totalLogins > 0 ? ((totalLogins - totalFailed) / totalLogins) * 100 : 0}%` }}
            />
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Top Browser</p>
          <div className="flex items-center gap-2">
            <Chrome className="w-5 h-5 text-gray-400" />
            <span className="text-lg font-semibold text-gray-900">{topBrowser}</span>
          </div>
          <div className="mt-2 space-y-1">
            {Object.entries(browserStats)
              .sort((a, b) => b[1] - a[1])
              .slice(0, 3)
              .map(([b, c]) => (
                <div key={b} className="flex items-center justify-between text-xs text-gray-500">
                  <span>{b}</span>
                  <span className="font-medium">{c}</span>
                </div>
              ))}
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Device Types</p>
          {(['desktop', 'mobile', 'tablet'] as const).map(d => {
            const count = records.filter(r => r.device_type === d).length;
            const pct = totalLogins > 0 ? Math.round((count / totalLogins) * 100) : 0;
            return (
              <div key={d} className="flex items-center gap-2 mb-1.5">
                <DeviceIcon type={d} />
                <span className="text-xs text-gray-600 capitalize w-14">{d}</span>
                <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full bg-[#40E0D0] rounded-full" style={{ width: `${pct}%` }} />
                </div>
                <span className="text-xs text-gray-500 w-8 text-right">{pct}%</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-100 p-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-48">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by email, company, or IP..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#40E0D0] text-gray-900"
            />
          </div>
          <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-lg">
            {(['24h', '7d', '30d', 'all'] as const).map(r => (
              <button
                key={r}
                onClick={() => setDateRange(r)}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  dateRange === r ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {r === 'all' ? 'All time' : r}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1.5">
            <Filter className="w-4 h-4 text-gray-400" />
            <select
              value={filterAlert}
              onChange={e => setFilterAlert(e.target.value as typeof filterAlert)}
              className="border border-gray-200 rounded-lg text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#40E0D0] text-gray-700 bg-white"
            >
              <option value="all">All users</option>
              <option value="warning">Warnings only</option>
              <option value="danger">Alerts only</option>
            </select>
          </div>
          <button
            onClick={fetchRecords}
            disabled={refreshing}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
            title="Refresh"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Active Alerts Banner */}
      {totalAlerts > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-amber-900">
                {totalAlerts} security alert{totalAlerts !== 1 ? 's' : ''} detected
              </p>
              <p className="text-xs text-amber-700 mt-0.5">
                Users with {ALERT_THRESHOLDS.failedAttempts}+ failed login attempts in 24h or {ALERT_THRESHOLDS.multipleIps}+ unique IPs are flagged.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Company Groups */}
      {loading ? (
        <div className="bg-white rounded-xl border border-gray-100 p-12 text-center">
          <RefreshCw className="w-8 h-8 text-gray-300 animate-spin mx-auto mb-3" />
          <p className="text-sm text-gray-500">Loading login records...</p>
        </div>
      ) : filteredGroups.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-100 p-12 text-center">
          <Shield className="w-12 h-12 text-gray-200 mx-auto mb-3" />
          <p className="text-sm font-medium text-gray-500">No login records found</p>
          <p className="text-xs text-gray-400 mt-1">Login events will appear here once users sign in.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredGroups.map(company => {
            const cKey = company.company_id || company.company_name;
            const isExpanded = expandedCompany === cKey;
            const hasAlerts = company.users.some(u => u.alert_level !== 'none');

            return (
              <div key={cKey} className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                {/* Company Header */}
                <button
                  className="w-full flex items-center gap-3 p-4 hover:bg-gray-50 transition-colors text-left"
                  onClick={() => setExpandedCompany(isExpanded ? null : cKey)}
                >
                  <div className="p-2 rounded-lg bg-gray-100">
                    <Building2 className="w-4 h-4 text-gray-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-gray-900 truncate">{company.company_name}</span>
                      {hasAlerts && (
                        <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0" />
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-0.5">
                      <span className="text-xs text-gray-500">
                        <Users className="w-3 h-3 inline mr-1" />
                        {company.users.length} user{company.users.length !== 1 ? 's' : ''}
                      </span>
                      <span className="text-xs text-gray-500">
                        <Activity className="w-3 h-3 inline mr-1" />
                        {company.total_logins} login{company.total_logins !== 1 ? 's' : ''}
                      </span>
                      <span className="text-xs text-gray-500">
                        <Globe className="w-3 h-3 inline mr-1" />
                        {company.unique_ips.length} IP{company.unique_ips.length !== 1 ? 's' : ''}
                      </span>
                      {company.failed_count > 0 && (
                        <span className="text-xs text-red-600 font-medium">
                          <XCircle className="w-3 h-3 inline mr-1" />
                          {company.failed_count} failed
                        </span>
                      )}
                    </div>
                  </div>
                  {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400 flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />}
                </button>

                {/* Company Users */}
                {isExpanded && (
                  <div className="border-t border-gray-100 divide-y divide-gray-50">
                    {company.users.map(user => {
                      const uKey = user.email || user.user_id || 'unknown';
                      const isUserExpanded = expandedUser === uKey;

                      return (
                        <div key={uKey}>
                          <button
                            className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left"
                            onClick={() => setExpandedUser(isUserExpanded ? null : uKey)}
                          >
                            <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
                              <span className="text-xs font-semibold text-teal-700">
                                {(user.email?.[0] || '?').toUpperCase()}
                              </span>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-sm font-medium text-gray-900 truncate">{user.email}</span>
                                {user.alert_level === 'danger' && (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700">
                                    <AlertTriangle className="w-3 h-3" /> High Risk
                                  </span>
                                )}
                                {user.alert_level === 'warning' && (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700">
                                    <AlertTriangle className="w-3 h-3" /> Warning
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                                <span className="text-xs text-gray-500">
                                  <Clock className="w-3 h-3 inline mr-1" />
                                  Last: {formatRelativeTime(user.last_login)}
                                </span>
                                <span className="text-xs text-gray-500">
                                  {user.records.length} login{user.records.length !== 1 ? 's' : ''}
                                </span>
                                <span className="text-xs text-gray-500">
                                  <Globe className="w-3 h-3 inline mr-1" />
                                  {user.unique_ips.length} IP{user.unique_ips.length !== 1 ? 's' : ''}
                                </span>
                                {user.failed_count > 0 && (
                                  <span className="text-xs text-red-600 font-medium">
                                    {user.failed_count} failed
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0">
                              {user.unique_ips.slice(0, 2).map(ip => (
                                <span key={ip} className="hidden sm:inline text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded font-mono">
                                  {ip}
                                </span>
                              ))}
                              {user.unique_ips.length > 2 && (
                                <span className="text-xs text-gray-400">+{user.unique_ips.length - 2}</span>
                              )}
                            </div>
                            {isUserExpanded ? <ChevronUp className="w-4 h-4 text-gray-400 flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />}
                          </button>

                          {/* User Login Records Table */}
                          {isUserExpanded && (
                            <div className="bg-gray-50/70 px-4 pb-3">
                              {/* IP Summary */}
                              <div className="mb-3 pt-3">
                                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">IP Addresses</p>
                                <div className="flex flex-wrap gap-2">
                                  {user.unique_ips.map(ip => {
                                    const ipRecords = user.records.filter(r => r.ip_address === ip);
                                    const ipFailed = ipRecords.filter(r => !r.success).length;
                                    return (
                                      <div key={ip} className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs border ${
                                        ipFailed > 0 ? 'bg-red-50 border-red-200 text-red-700' : 'bg-white border-gray-200 text-gray-600'
                                      }`}>
                                        <MapPin className="w-3 h-3" />
                                        <span className="font-mono">{ip}</span>
                                        <span className="text-gray-400">·</span>
                                        <span>{ipRecords.length}x</span>
                                        {ipFailed > 0 && <span className="text-red-500">({ipFailed} fail)</span>}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>

                              {/* Login History */}
                              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Login History</p>
                              <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
                                <table className="min-w-full text-xs">
                                  <thead className="bg-gray-50 border-b border-gray-200">
                                    <tr>
                                      <th className="px-3 py-2 text-left font-semibold text-gray-500">Time</th>
                                      <th className="px-3 py-2 text-left font-semibold text-gray-500">IP Address</th>
                                      <th className="px-3 py-2 text-left font-semibold text-gray-500">Device</th>
                                      <th className="px-3 py-2 text-left font-semibold text-gray-500">Browser / OS</th>
                                      <th className="px-3 py-2 text-left font-semibold text-gray-500">Status</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-gray-100">
                                    {user.records.slice(0, 50).map(r => (
                                      <tr key={r.id} className={`${!r.success ? 'bg-red-50/50' : ''}`}>
                                        <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                                          {formatDate(r.created_at)}
                                        </td>
                                        <td className="px-3 py-2 font-mono text-gray-700 whitespace-nowrap">
                                          {r.ip_address || '—'}
                                        </td>
                                        <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                                          <span className="flex items-center gap-1">
                                            <DeviceIcon type={r.device_type} />
                                            <span className="capitalize">{r.device_type}</span>
                                          </span>
                                        </td>
                                        <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                                          {r.browser && r.os ? `${r.browser} / ${r.os}` : r.browser || r.os || '—'}
                                        </td>
                                        <td className="px-3 py-2 whitespace-nowrap">
                                          {r.success ? (
                                            <span className="inline-flex items-center gap-1 text-green-700 font-medium">
                                              <CheckCircle className="w-3.5 h-3.5" /> Success
                                            </span>
                                          ) : (
                                            <span className="inline-flex items-center gap-1 text-red-600 font-medium" title={r.failure_reason}>
                                              <XCircle className="w-3.5 h-3.5" /> Failed
                                              {r.failure_reason && (
                                                <span className="text-red-400 font-normal"> — {r.failure_reason.slice(0, 30)}</span>
                                              )}
                                            </span>
                                          )}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                                {user.records.length > 50 && (
                                  <p className="text-center text-xs text-gray-400 py-2">
                                    Showing 50 of {user.records.length} records
                                  </p>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
